import {create, type TSetter} from "@/store/createStore";
import type {TAgency} from "@/store/types";
import {withStreetNumberCheck} from "@/utils/addressUtils";
import isEqual from "lodash/isEqual";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const AgencySchema = withStreetNumberCheck({
  countryKey: "country",
  schema: z.object({
    city: z
      .string({error: "company_profile:missing_address_city_error"})
      .min(1, "company_profile:missing_address_city_error"),
    address: z
      .string({error: "company_profile:missing_address_street_error"})
      .min(1, "company_profile:missing_address_street_error"),
    country: z.string().nullish(),
    postal_code: z
      .string({error: "company_profile:missing_address_postal_error"})
      .min(1, "company_profile:missing_address_postal_error"),
    name: z.string().min(1, "agency_profile:missing_name_error"),
    website: z.string().min(1, "company_profile:missing_website_error"),
  }),
  streetKey: "address",
});

type TState = {
  agency: TAgency;
  errors: Record<string, string> | null;
  savedAgency: TAgency;
  discardChanges(): void;
  hasUnsavedChanges(): boolean;
  setter: TSetter<TState>;
  agencyInfoValidation(): boolean;
  reset(): void;
  setAgency(data: TAgency): void;
};

const initialAgencyState = {} as TAgency;

const createAgencyStore = (name: string) =>
  create<TState>()(
    immer(
      devtools(
        (set, get) => ({
          agency: initialAgencyState,
          errors: null,
          savedAgency: initialAgencyState,
          discardChanges: () =>
            set(
              state => {
                state.agency = state.savedAgency;
                state.errors = null;
              },
              false,
              `${name}/discardChanges`,
            ),
          hasUnsavedChanges: () => !isEqual(get().agency, get().savedAgency),
          setter: fn => set(fn, false, `${name}/setter`),
          agencyInfoValidation: () => {
            const result = AgencySchema.safeParse(get().agency);

            set(state => {
              state.errors = null;
            });

            if (!result.success) {
              result.error.issues.map(issue =>
                set(state => {
                  if (state.errors) {
                    state.errors[issue.path[0].toString()] = issue.message;
                  } else {
                    state.errors = {[issue.path[0]]: issue.message};
                  }
                }),
              );
            }

            return result.success;
          },
          reset: () =>
            set(
              state => {
                state.agency = initialAgencyState;
                state.savedAgency = initialAgencyState;
              },
              false,
              `${name}/reset`,
            ),
          setAgency: data =>
            set(
              state => {
                state.agency = data;
                state.savedAgency = data;
              },
              false,
              `${name}/setAgency`,
            ),
        }),
        {name},
      ),
    ),
  );

export const useAgencyStore = createAgencyStore("agencyStore");
export const useSettingsAgencyStore = createAgencyStore("settingsAgencyStore");
